import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { ActivateForm } from 'components/user/ActivateForm';
import { ForgotPasswordForm } from 'components/user/forgotten_password/ForgotPasswordForm';
import { LoginForm } from 'components/user/LoginForm';
import { TwoFactorForm } from 'components/user/TwoFactorForm';
import { SignUpForm } from 'components/user/SignUpForm';
import { InlineAuthStage } from './hooks/useSummaryGuestConversion';

export interface InlineAuthFormsProps {
    authStage: InlineAuthStage;
    loginDefaultIdentity: string | undefined;
    loginDefaultTab: 'email' | 'phone';
    defaultPhone: string | undefined;
    defaultFirstName: string | undefined;
    defaultLastName: string | undefined;
    inlineDefaultIdentity: string | undefined;
    preferIdentityType: 'email' | 'phone';
    twoFactorIdentity: string | undefined;
    onAuthComplete: () => void;
    onSwitchToSignUp: () => void;
    onSwitchToLogIn: () => void;
    onSwitchToForgotPassword: () => void;
    onSwitchToTwoFactor: (identity: string) => void;
    onSignupSuccess: () => void;
}

export const InlineAuthForms: React.FC<InlineAuthFormsProps> = ({
    authStage,
    loginDefaultIdentity,
    loginDefaultTab,
    defaultPhone,
    defaultFirstName,
    defaultLastName,
    inlineDefaultIdentity,
    preferIdentityType,
    twoFactorIdentity,
    onAuthComplete,
    onSwitchToSignUp,
    onSwitchToLogIn,
    onSwitchToForgotPassword,
    onSwitchToTwoFactor,
    onSignupSuccess
}) => {
    const { t } = useTranslation();

    return (
        <>
            {authStage === 'login' && (
                <Box width="100%">
                    <LoginForm
                        defaultIdentity={loginDefaultIdentity}
                        defaultPhone={defaultPhone}
                        defaultTab={loginDefaultTab}
                        noPadding
                        compact
                        onSuccess={onAuthComplete}
                        onSwitchToSignUp={onSwitchToSignUp}
                        onSwitchToForgotPassword={onSwitchToForgotPassword}
                        onSwitchToTwoFactor={onSwitchToTwoFactor}
                        onSwitchToActivate={onSignupSuccess}
                    />
                </Box>
            )}
            {authStage === 'signup' && (
                <Box width="100%">
                    <SignUpForm
                        defaultIdentity={inlineDefaultIdentity}
                        defaultFirstName={defaultFirstName}
                        defaultLastName={defaultLastName}
                        noPadding
                        onSuccess={onSignupSuccess}
                        onSwitchToLogIn={onSwitchToLogIn}
                        preferIdentityType={preferIdentityType}
                    />
                </Box>
            )}
            {authStage === 'activate' && (
                <Box width="100%">
                    <Typography align="center" sx={{ pb: 1, fontWeight: 700 }}>
                        {t('ONBOARDING_ACTIVATION_TITLE')}
                    </Typography>
                    <ActivateForm noPadding onSuccess={onAuthComplete} />
                </Box>
            )}
            {authStage === 'forgot-password' && (
                <Box width="100%">
                    <ForgotPasswordForm
                        defaultIdentity={loginDefaultIdentity}
                        noPadding
                        onSuccess={onAuthComplete}
                        onSwitchToLogIn={onSwitchToLogIn}
                    />
                </Box>
            )}
            {authStage === 'two-factor' && twoFactorIdentity && (
                <Box width="100%">
                    <TwoFactorForm identity={twoFactorIdentity} noPadding onSuccess={onAuthComplete} />
                </Box>
            )}
        </>
    );
};
